import { type FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getKnownViewColumns, getViewColumns, type ViewDefinition } from '../config/viewsConfig';
import { EntityFilters } from './EntityFilters';
import { EntityTable } from './EntityTable';
import { CopyLinkButton } from './CopyLinkButton';
import { Breadcrumb } from './Breadcrumb';
import { ViewSearchBar } from './ViewSearchBar';
import { useViewState } from '../hooks/useViewState';
import { useLab, entityHasLabId } from '../LabContext';
import './FilteredView.css';
import './EntityView.css';

interface FilteredViewProps {
  view: ViewDefinition;
}

export const FilteredView: FC<FilteredViewProps> = ({ view }) => {
  const navigate = useNavigate();
  const { labFilters, labSelection } = useLab();
  const hasLabId = entityHasLabId(view.entityType);

  // Default-visible columns can vary by lab (e.g. Copper One's focused ICP set).
  const defaultColumns = useMemo(() => getViewColumns(view, labSelection), [view, labSelection]);
  const knownColumns = useMemo(() => getKnownViewColumns(view), [view]);

  const discoveryExtraFilters = useMemo(() => [
    ...(hasLabId ? labFilters : []),
    ...view.filters,
  ], [hasLabId, labFilters, view.filters]);

  const {
    entities,
    resolvedEntities,
    pagination,
    filters,
    sortBy,
    sortOrder,
    loading,
    error,
    handleFiltersChange,
    handlePageChange,
    handleLimitChange,
    handleSortChange,
    visibleColumns,
    setVisibleColumns,
    searchQuery,
    setSearchQuery,
    allColumns,
    fetchAllEntities,
  } = useViewState({
    entityType: view.entityType,
    allColumns: defaultColumns,
    optionalColumns: view.optionalColumns,
    initialFilters: view.filters,
    defaultSort: view.defaultSort,
    viewId: view.id,
    extraFilters: hasLabId ? labFilters : undefined,
  });

  const handleEntityClick = (entityId: number) => {
    navigate(`/${view.entityType}/${entityId}`);
  };

  return (
    <div className="entity-view">
      <div className="entity-view-header">
        <Breadcrumb items={[{ label: 'Mariana Labs', path: '/dashboard' }, { label: 'Views' }, { label: view.label }]} />
        {view.description && <p className="fv-description">{view.description}</p>}
      </div>

      {error && (
        <div className="error-message">Error: {error}</div>
      )}

      <div className="entity-filters-row">
        <EntityFilters
          entityType={view.entityType}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          entities={entities}
          extraFilters={discoveryExtraFilters}
          knownColumns={knownColumns}
        />
        <CopyLinkButton />
      </div>

      <ViewSearchBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        loading={loading && searchQuery.trim().length > 0}
      />

      <EntityTable
        entities={entities}
        entityType={view.entityType}
        columns={visibleColumns}
        defaultColumns={allColumns}
        optionalColumns={view.optionalColumns}
        onColumnsChange={setVisibleColumns}
        resolvedEntities={resolvedEntities}
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onEntityClick={handleEntityClick}
        onSortChange={handleSortChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        loading={loading}
        exportName={`${view.id}-${view.entityType}s`}
        sampleEntities={entities}
        extraFilters={discoveryExtraFilters}
        onExportAll={fetchAllEntities}
        groupByKey={view.groupByKey}
      />
    </div>
  );
};
